import {inject, InjectionToken, Pipe, PipeTransform} from '@angular/core';
import {
    DANISH_DATE_SEPARATOR,
    dateTimeFormatOptions,
    formatDateTime,
    TranslateService,
} from '@brunata-angular/util';
import {DateTime} from 'luxon';

/**
 * App-level default format for the `appdatetime` pipe. When no token is provided and the template
 * passes no explicit format, the pipe falls back to the locale numeric date-time format
 * (`dateTimeFormatOptions`, e.g. "08.06.2026 14.30.00"). An app that wants a different default
 * provides this token at the root — ngwebbill provides `DateTime.DATETIME_MED_WITH_SECONDS`
 * ("8 Jun 2026, 14.30.00") to preserve the format its templates relied on before migrating off
 * the app-local pipe. Individual call sites can opt back into the numeric format with the
 * `'numeric'` shorthand (`{{ value | appdatetime: 'numeric' }}`).
 */
export const DATE_TIME_PIPE_DEFAULT_FORMAT = new InjectionToken<Intl.DateTimeFormatOptions>(
    'DATE_TIME_PIPE_DEFAULT_FORMAT',
);

@Pipe({
    name: 'appdatetime',
    standalone: true,
})
export class DateTimePipe implements PipeTransform {
    private readonly translateService = inject(TranslateService);
    private readonly defaultFormat = inject(DATE_TIME_PIPE_DEFAULT_FORMAT, {optional: true});
    private readonly danishDateSeparator = inject(DANISH_DATE_SEPARATOR);

    transform(
        value: DateTime | null | undefined,
        dateFormat?: Intl.DateTimeFormatOptions | 'relative' | 'numeric',
        convertToLocal = false,
    ): string {
        if (value == null || !value.isValid) {
            return '';
        }
        // Convert UTC DateTime to local timezone for display if convertToLocal is true
        const dateTime = convertToLocal ? value.toLocal() : value;

        // Support relative time format (e.g., "2 hours ago", "in 3 days")
        if (dateFormat === 'relative') {
            return dateTime.toRelative({locale: this.translateService.language}) ?? '';
        }

        // 'numeric' forces the locale numeric format, regardless of the app-default token.
        const formatOptions =
            dateFormat === 'numeric'
                ? dateTimeFormatOptions
                : (dateFormat ?? this.defaultFormat ?? undefined);
        return formatDateTime(
            dateTime,
            this.translateService.language,
            formatOptions,
            this.danishDateSeparator,
        );
    }
}
